import React, { useEffect, useState } from "react";
import { SupportToolsMenu } from "./SupportToolsMenu";
import { SUPPORT_TOOLS } from "./supportTools";
import { SUPPORT_PALETTE } from "./supportPalette";

// Style guide responsive breakpoint — React.useState + resize listener, no
// media queries in inline styles.
const NARROW_BREAKPOINT = 768;

function useIsWide(breakpoint = NARROW_BREAKPOINT) {
  const [isWide, setIsWide] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= breakpoint,
  );
  useEffect(() => {
    function onResize() { setIsWide(window.innerWidth >= breakpoint); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isWide;
}

const FOCUS_RING_STYLE = `
  .support-header-control:focus-visible {
    outline: 2px solid ${SUPPORT_PALETTE.focusRing};
    outline-offset: 2px;
  }
  .support-header-control:hover {
    background: ${SUPPORT_PALETTE.actionHoverBg};
  }
  /* Declared after .support-header-control so the exit keeps its neutral tint
     on hover instead of picking up the pink action tint. */
  .support-header-exit:hover {
    background: ${SUPPORT_PALETTE.exitHoverBg};
  }
  .support-tools-menu-item:focus-visible {
    outline: 2px solid ${SUPPORT_PALETTE.focusRing};
    outline-offset: -2px;
  }
  .support-tools-search::placeholder {
    color: ${SUPPORT_PALETTE.textSecondary};
  }
  .support-tools-search:focus-visible {
    outline: 2px solid ${SUPPORT_PALETTE.focusRing};
    outline-offset: 2px;
  }
`;

// Support masquerade header. Left-to-right: context (who's being viewed),
// the pending-changes alert (when applicable), actions (Advertiser settings
// + the Tools menu), and Leave view last. See src/app/supportTools.js for
// how to add a new tool to the Tools menu.
export function SupportHeader({
  masqueradeAdvertiserId,
  effectiveAdvertiserName,
  isSupportUser,
  pendingSettingsRow,
  clearMasquerade,
  onOpenAdvertiserSettings,
  onOpenApprovalModal,
  onOpenChangeHistory,
  onOpenCpaForecast,
  tools = SUPPORT_TOOLS,
}) {
  const isWide = useIsWide();

  if (masqueradeAdvertiserId == null) return null;

  const displayName = effectiveAdvertiserName || "Advertiser";
  const fullContextLabel = `${displayName} (${masqueradeAdvertiserId})`;

  const ctx = {
    setAdvertiserSettings: onOpenAdvertiserSettings,
    setChangeHistory: onOpenChangeHistory,
    setCpaForecast: onOpenCpaForecast,
  };

  const advertiserSettingsTool = {
    id: "advertiser-settings",
    label: "Advertiser settings",
    group: "Account",
    isVisible: () => true,
    onSelect: (c) => c.setAdvertiserSettings(true),
  };

  const menuTools = isWide ? tools : [advertiserSettingsTool, ...tools];

  return (
    <div style={{ background: SUPPORT_PALETTE.bg, borderBottom: SUPPORT_PALETTE.borderBottom, padding: "0 20px", height: 44, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap", overflow: "hidden" }}>
      <style>{FOCUS_RING_STYLE}</style>

      {/* Context zone */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
        <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: SUPPORT_PALETTE.textStrong, background: SUPPORT_PALETTE.ghostBg, border: SUPPORT_PALETTE.ghostBorder, borderRadius: 6, padding: "3px 8px" }}>
          Support view
        </span>
        <span
          title={fullContextLabel}
          style={{ fontSize: 13, color: SUPPORT_PALETTE.textSecondary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          Viewing <strong style={{ color: SUPPORT_PALETTE.accentText }}>{displayName}</strong>
          {" "}
          <span>{`(${masqueradeAdvertiserId})`}</span>
        </span>
      </div>

      {/* Alert zone */}
      {isSupportUser && pendingSettingsRow && (
        <button
          type="button"
          className="support-header-control"
          onClick={onOpenApprovalModal}
          style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, background: SUPPORT_PALETTE.amberBg, border: SUPPORT_PALETTE.amberBorder, borderRadius: 6, color: SUPPORT_PALETTE.amberText, fontSize: 12, fontWeight: 600, padding: "5px 10px", cursor: "pointer" }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          </svg>
          1 pending change
        </button>
      )}

      {/* Actions zone */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {isSupportUser && isWide && (
          <button
            type="button"
            className="support-header-control"
            onClick={onOpenAdvertiserSettings}
            style={{ background: SUPPORT_PALETTE.actionBg, border: SUPPORT_PALETTE.actionBorder, borderRadius: 6, color: SUPPORT_PALETTE.actionText, fontSize: 12, fontWeight: 600, padding: "6px 12px", cursor: "pointer" }}
          >
            Advertiser settings
          </button>
        )}
        {isSupportUser && menuTools.length > 0 && (
          <SupportToolsMenu tools={menuTools} ctx={ctx} />
        )}
        <button
          type="button"
          className="support-header-control support-header-exit"
          onClick={clearMasquerade}
          style={{ padding: "6px 14px", background: SUPPORT_PALETTE.exitBg, color: SUPPORT_PALETTE.exitText, border: SUPPORT_PALETTE.exitBorder, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
        >
          Leave view
        </button>
      </div>
    </div>
  );
}
